import { MapMouseEvent, MapTouchEvent } from 'mapbox-gl';

import {
  EDossiers,
  EMenuItems,
  setSelectedDossier,
  setSelectedOption,
} from '../../../main-menu/menu.slice';
import { Drawing, DrawingShape } from '../../../models/drawings/drawings.model';
import { setSelectedDrawing } from '../../../state/drawings/drawings.slice';
import store from '../../../store';
import GeoHelper from '../../../utils/geo-helpers.utils';
import MapHelpers from '../../map.utils';
import addLineLayer from '../layer-utils/add-line-layer';
import { addPointImageLayer } from '../layer-utils/add-point-layer';
import addPolygonLayer from '../layer-utils/add-polygon-layer';
import addSimpleGeoJsonSource from '../layer-utils/add-simple-geojson-source';
import onLayerClick from '../layer-utils/layer-click-handler';
import MapLayer, { MapGroupLayer } from '../map-layer.enum';
import MapLayerPaint from '../map-layer-paint';

const addDrawingLayer = (drawingId: string, data: GeoJSON.Feature) => {
  const onClick = (e: MapMouseEvent | MapTouchEvent) => {
    const { drawings } = store.getState().drawings;
    const features = MapHelpers.queryRenderedFeatures(e.point, {
      layers: [drawingId],
    });
    const mapFeatures = MapHelpers.queryRenderedFeatures(e.point, {
      percent: 2,
    }).filter((item) => item.sourceLayer !== 'water');
    const areaClicked = drawings?.find(
      (drawing) => drawing.region_id === features[0].properties!.region_id
    );

    if (mapFeatures.length <= 1) {
      store.dispatch(setSelectedOption(EMenuItems.DRAWINGS));
      store.dispatch(setSelectedDrawing(areaClicked as Drawing));
      store.dispatch(setSelectedDossier(EDossiers.DRAWING));

      window.history.pushState({}, '', `/drawings/${areaClicked?.region_id}`);
    }
  };

  const drawingColour = data.properties?.color;
  const drawingOpacity = data.properties?.opacity;

  const shape: DrawingShape = data.properties?.subType || DrawingShape.Polygon;

  // biome-ignore lint/correctness/noUnusedInstantiation: fire-and-forget Promise used for its side effects
  new Promise((resolve) => {
    const geojson =
      shape === DrawingShape.Circle
        ? GeoHelper.convertFeatureCircleToPolygon(
            <GeoJSON.Feature<GeoJSON.Point>>data
          )
        : data;

    const geojsonWithDrawingId = {
      ...geojson,
      properties: {
        ...geojson.properties,
        region_id: drawingId,
      },
    };

    if (MapHelpers.getSource(drawingId)) {
      GeoHelper.setMapboxGeoJSONSourceData(drawingId, geojsonWithDrawingId);
      if (shape === DrawingShape.Line) {
        MapHelpers.updateLayerPaintProperty(
          drawingId,
          'line-color',
          drawingColour
        );
      } else if (shape === DrawingShape.Point) {
        MapHelpers.updateLayerPaintProperty(
          drawingId,
          'circle-color',
          drawingColour
        );
      } else {
        MapHelpers.updateLayerPaintProperty(
          drawingId,
          'fill-color',
          drawingColour
        );
        MapHelpers.updateLayerPaintProperty(
          drawingId,
          'fill-opacity',
          drawingOpacity
        );
      }
      return;
    }

    addSimpleGeoJsonSource(drawingId);

    if (shape === DrawingShape.Line) {
      addLineLayer(
        drawingId,
        MapLayerPaint.DRAWING_LINE_DEFAULT_PAINT,
        MapGroupLayer.DRAWINGS,
        drawingColour
      );
    } else if (shape === DrawingShape.Point) {
      addPointImageLayer(
        drawingId,
        [
          (geojson.geometry as any).coordinates[0],
          (geojson.geometry as any).coordinates[1],
        ],
        drawingColour,
        ' ',
        12,
        MapGroupLayer.DRAWINGS
      );
    } else {
      addPolygonLayer(
        drawingId,
        MapLayerPaint.DRAWING_SHAPE_DEFAULT_PAINT,
        MapGroupLayer.DRAWINGS,
        drawingColour,
        drawingOpacity
      );
    }
    GeoHelper.setMapboxGeoJSONSourceData(drawingId, geojsonWithDrawingId);

    MapHelpers.addMapEventListener('click', drawingId, onLayerClick(onClick));

    MapHelpers.moveLayerIfExists(
      drawingId,
      MapLayer.CURRENTLY_ALERTING_VESSELS
    );
    resolve(MapHelpers);
  });
};

export default addDrawingLayer;
